import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import FighterRadialChart from './FighterRadialChart';
import { FightPrediction, StatisticalInsightsObject } from '../types';
import { useFightData } from '../contexts/FightDataContext';
import { useThemeCompat } from '../contexts/ThemeContext';
import { 
  DiceThree, 
  HandFist, 
  SmileyXEyes,  
  Strategy, 
  ShieldStar,
  CaretUp,
  CaretDown,
  Target,
  Lightning,
  Shield,
  Armchair
} from 'phosphor-react';

// Function to convert decimal odds to American odds
const convertToAmericanOdds = (decimalOdds: number): string => {
  // Full validation of input
  if (typeof decimalOdds !== 'number' || isNaN(decimalOdds)) {
    console.warn("Invalid decimal odds (not a number):", decimalOdds);
    return "N/A";
  }
  
  // Decimal odds must be greater than 1.0
  if (decimalOdds <= 1.0) {
    console.warn("Invalid decimal odds (must be > 1.0):", decimalOdds);
    return "N/A";
  }
  
  try {
    // Convert decimal to American format
    if (decimalOdds >= 2.0) {
      // Underdog: Positive American odds (decimal - 1) * 100
      const americanOdds = Math.round((decimalOdds - 1) * 100);
      return `+${americanOdds}`;
    } else {
      // Favorite: Negative American odds -100 / (decimal - 1)
      const americanOdds = Math.round(-100 / (decimalOdds - 1));
      return `${americanOdds}`; // Minus sign included in calculation
    }
  } catch (e) {
    console.error("Error converting decimal odds:", decimalOdds, e);
    return "N/A";
  }
};

// Stat Card component for individual fighter stats
interface StatCardProps {
  statName: string;
  value: number | string;
  otherValue: number | string;
  unit?: string;
  icon: React.ReactNode;
  color: string;
  isWinning: boolean;
  position: 'first' | 'middle' | 'last' | 'single';
}

const StatCard: React.FC<StatCardProps> = ({ 
  statName, 
  value, 
  otherValue, 
  unit, 
  icon, 
  color, 
  isWinning,
  position
}) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  
  // Get access to selectedFightIndex to force animation reset when it changes
  const { selectedFightIndex } = useFightData();

  // Identify if this is an odds metric
  const isOddsMetric = statName.toLowerCase().includes('odds');
  
  // Parse values to numbers for comparison but handle odds specially
  let valueNum: number;
  let otherValueNum: number;
  
  if (isOddsMetric) {
    // For odds, check if already in American format (string starting with + or -)
    if (typeof value === 'string' && (value.startsWith('+') || value.startsWith('-'))) {
      // Already in American format, store as is for display but create implied probability
      // For calculating bar length, we'll use implied probabilities
      if (value === 'N/A') {
        valueNum = 0;
      } else if (value.startsWith('+')) {
        // Convert from "+136" format to implied probability
        const americanOdds = parseInt(value.substring(1), 10);
        valueNum = 100 / (americanOdds + 100); // Underdog implied probability
      } else {
        // Convert from "-149" format to implied probability
        const americanOdds = parseInt(value.substring(1), 10); // Remove the - sign
        valueNum = americanOdds / (americanOdds + 100); // Favorite implied probability
      }
    } else {
      // Decimal odds or numeric value - parse it if string
      valueNum = typeof value === 'number' ? value : parseFloat(value as string) || 0;
    }
    
    // Same logic for other value
    if (typeof otherValue === 'string' && (otherValue.startsWith('+') || otherValue.startsWith('-'))) {
      if (otherValue === 'N/A') {
        otherValueNum = 0;
      } else if (otherValue.startsWith('+')) {
        const americanOdds = parseInt(otherValue.substring(1), 10);
        otherValueNum = 100 / (americanOdds + 100);
      } else {
        const americanOdds = parseInt(otherValue.substring(1), 10);
        otherValueNum = americanOdds / (americanOdds + 100);
      }
    } else {
      otherValueNum = typeof otherValue === 'number' ? otherValue : parseFloat(otherValue as string) || 0;
    }
  } else {
    // For non-odds metrics, simply parse to numbers
    valueNum = typeof value === 'number' ? value : parseFloat(value as string) || 0;
    otherValueNum = typeof otherValue === 'number' ? otherValue : parseFloat(otherValue as string) || 0;
  }
  
  // Calculate percentage for the bar
  let percentage: number;
  
  if (isOddsMetric) {
    // For odds, use implied probabilities to determine bar length
    // Higher implied probability (better chance of winning) = longer bar
    const MIN_BAR_PERCENTAGE = 10; // Minimum bar size
    const MAX_BAR_PERCENTAGE = 90; // Maximum bar size
    
    // Handle the case where both values are 0 (N/A)
    if (valueNum === 0 && otherValueNum === 0) {
      percentage = 50; // Equal bars for N/A
    } else if (valueNum === 0) {
      percentage = MIN_BAR_PERCENTAGE; // Minimum bar for N/A when other has a value
    } else if (otherValueNum === 0) {
      percentage = MAX_BAR_PERCENTAGE; // Maximum bar when other is N/A
    } else {
      const totalImpliedProb = valueNum + otherValueNum;
      percentage = MIN_BAR_PERCENTAGE + 
                  ((MAX_BAR_PERCENTAGE - MIN_BAR_PERCENTAGE) * (valueNum / totalImpliedProb));
    }
  } else {
    // For regular stats, higher is better, calculate normally
    const maxPossibleValue = Math.max(valueNum, otherValueNum) * 1.2; // Add 20% buffer
    percentage = maxPossibleValue === 0 ? 0 : (valueNum / maxPossibleValue) * 100;
  }
  
  // Animation function with easing
  const animateBar = (timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = timestamp - startTimeRef.current;
    const duration = 800; // Same duration as radial charts
    
    // Cubic easing in/out for smoother motion (same as radial charts)
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = progress < 0.5
      ? 4 * progress * progress * progress
      : 1 - Math.pow(-2 * progress + 2, 3) / 2;
    
    const currentPercentage = easedProgress * percentage;
    setAnimatedPercentage(currentPercentage);
    
    if (elapsed < duration) {
      animationRef.current = requestAnimationFrame(animateBar);
    } else {
      setAnimatedPercentage(percentage); // Ensure we end at exactly the target percentage
      animationRef.current = null;
      startTimeRef.current = null;
    }
  };

  // Start animation when component mounts
  useEffect(() => {
    // Clean up previous animation if it exists
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    // Reset to zero and start animation
    setAnimatedPercentage(0);
    startTimeRef.current = null;
    animationRef.current = requestAnimationFrame(animateBar);
    
    // Clean up on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  // Add selectedFightIndex to dependencies to force animation reset when changing fighters
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentage, selectedFightIndex]); // Re-animate when the percentage or selectedFightIndex changes
  
  // Format the display value for presentation
  let displayValue;
  
  if (isOddsMetric) {
    // For odds, just use the raw value, no conversion needed
    // The conversion should have already happened upstream
    displayValue = value;
  } else if (unit === '%') {
    // For percentage values, show just the number without duplicate %
    displayValue = valueNum;
  } else if (unit === 'per min') {
    // For per minute values, show just the number without "per min" in the value
    displayValue = valueNum;
  } else {
    displayValue = value;
  }
  
  // Get abbreviated label for mobile
  const getAbbreviatedLabel = (label: string) => {
    // Only abbreviate on mobile
    if (window.innerWidth >= 768) {
      return label;
    }

    switch (label.toLowerCase()) {
      case 'betting odds':
        return 'Odds';
      case 'strikes landed per minute':
        return 'SLPM';
      case 'strikes absorbed per minute':
        return 'SAPM';
      case 'takedown accuracy':
        return 'TD Acc';
      case 'striking accuracy':
        return 'Strike Acc';
      case 'striking defense':
        return 'Strike Def';
      case 'takedown defense':
        return 'TD Def';
      default:
        return label;
    }
  };
  
  // Apply position-specific border radius
  let borderRadiusClasses = '';
  switch (position) {
    case 'first':
      borderRadiusClasses = 'rounded-t-3xl rounded-b-md';
      break;
    case 'middle':
      borderRadiusClasses = 'rounded-md';
      break;
    case 'last':
      borderRadiusClasses = 'rounded-t-md rounded-b-3xl';
      break;
    case 'single':
      borderRadiusClasses = 'rounded-3xl';
      break;
  }
  
  // Check if we're on mobile
  const isMobile = window.innerWidth < 768;
  
  return (
    <div className={`shadcn-card ${borderRadiusClasses} p-4 md:p-6 flex flex-col space-y-2`}>
      {/* Top row with label and icon */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-text-tertiary dark:text-dark-text-tertiary">
          {isMobile ? getAbbreviatedLabel(statName) : statName}
        </div>
        <div className="text-text-tertiary dark:text-dark-text-tertiary">
          {icon}
        </div>
      </div>
      
      {/* Stat value */}
      <div className="text-2xl font-bold text-text-primary dark:text-dark-text-primary">
        {displayValue}{isOddsMetric ? '' : unit === 'per min' ? '' : unit}
      </div>
      
      {/* Mini bar chart with animation */}
      <div className="h-1.5 bg-zinc-200 dark:bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ 
            width: `${animatedPercentage}%`,
            backgroundColor: color
          }}
        ></div>
      </div>
    </div>
  );
};

const FighterComparison: React.FC = () => {
  const { fightPredictions, selectedFightIndex, setSelectedFightIndex, isLoading } = useFightData();
  const { theme } = useThemeCompat();
  
  // Handle navigation between fighters with useCallback
  const navigateToPrevFighter = useCallback(() => {
    if (selectedFightIndex > 0) {
      setSelectedFightIndex(selectedFightIndex - 1);
      // Remove focus from any focused element
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }, [selectedFightIndex, setSelectedFightIndex]);

  const navigateToNextFighter = useCallback(() => {
    if (selectedFightIndex < fightPredictions.length - 1) {
      setSelectedFightIndex(selectedFightIndex + 1);
      // Remove focus from any focused element
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }, [selectedFightIndex, setSelectedFightIndex, fightPredictions.length]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault(); // Prevent page scrolling
          navigateToPrevFighter();
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault(); // Prevent page scrolling
          navigateToNextFighter();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigateToPrevFighter, navigateToNextFighter]);
  
  // If no fight predictions are loaded yet, return null
  if (!fightPredictions || !fightPredictions.length) {
    return null;
  }
  
  // If no fight is selected (mobile view with sheet closed), return null
  if (selectedFightIndex === -1) {
    return null;
  }
  
  const selectedFight: FightPrediction = fightPredictions[selectedFightIndex];
  
  const { fighter1, fighter2, explanation, narrative, importantStats } = selectedFight;
  
  // Use gray color for both fighters (for all data visualization)
  const grayColor = theme === 'light' ? '#71717a' : '#71717a'; // Same zinc-500 for both themes
  
  // Still use different winner colors for the win probability charts - with stronger blue for better mobile visibility
  const fighter1WinnerColor = fighter1.winProbability > fighter2.winProbability ? 
    (theme === 'light' ? '#0066ff' : '#4d94ff') : // Brighter blues for better visibility on mobile
    grayColor; // gray for loser
  
  const fighter2WinnerColor = fighter2.winProbability > fighter1.winProbability ? 
    (theme === 'light' ? '#0066ff' : '#4d94ff') : // Brighter blues for better visibility on mobile
    grayColor; // gray for loser

  // Define the type for the importantStats
  type StatItem = {
    name: string;
    fighter1Value: number | string;
    fighter2Value: number | string;
    unit?: string;
  };

  // Helper function to find a stat by name pattern
  const findStatByPattern = (pattern: string): number => {
    return importantStats.findIndex(stat => 
      stat.name.toLowerCase().includes(pattern.toLowerCase())
    );
  };

  // 1. Find odds stat (prioritizing exact betting odds matches)
  const oddsStatIndex = importantStats.findIndex(stat => 
    stat.name === "Betting Odds" || 
    (stat.name.toLowerCase().includes('odds') && !stat.name.toLowerCase().includes('differential'))
  );
  
  // 2. Find the other required stats with more specific naming
  const strikesLandedIndex = findStatByPattern('strikes landed');
  const strikesAbsorbedIndex = findStatByPattern('strikes absorbed');
  const takedownAccuracyIndex = findStatByPattern('takedown accuracy');
  const strikingAccuracyIndex = findStatByPattern('striking accuracy');
  
  // Log the stats we found for debugging
  console.log("Found stat indices:", {
    oddsStatIndex,
    strikesLandedIndex,
    strikesAbsorbedIndex,
    takedownAccuracyIndex,
    strikingAccuracyIndex
  });
  
  // 3. Create a new ordered array with the stats in the required order
  const orderedStats: StatItem[] = [];
  
  // Add the odds stat first - with special handling for odds formatting
  if (oddsStatIndex !== -1) {
    // Clone the stat to avoid modifying the original
    const oddsStat = { ...importantStats[oddsStatIndex] };
    
    // Ensure the name is "Odds" for consistent display
    oddsStat.name = "Odds";
    
    // For odds values, make sure they're proper American odds format
    // Make sure fighter1 odds are properly converted
    if (typeof oddsStat.fighter1Value === 'number') {
      // Direct conversion from decimal odds
      oddsStat.fighter1Value = convertToAmericanOdds(oddsStat.fighter1Value);
      console.log(`Converted fighter1 odds from decimal ${importantStats[oddsStatIndex].fighter1Value} to American ${oddsStat.fighter1Value}`);
    } else if (typeof oddsStat.fighter1Value === 'string') {
      // Check if already in American format (starts with + or -)
      if (oddsStat.fighter1Value.startsWith('+') || oddsStat.fighter1Value.startsWith('-')) {
        // Already in American format, don't convert
        console.log(`Fighter1 odds already in American format: ${oddsStat.fighter1Value}`);
      } else {
        // Try to parse as decimal number then convert
        const num = parseFloat(oddsStat.fighter1Value);
        if (!isNaN(num) && num > 1) {
          oddsStat.fighter1Value = convertToAmericanOdds(num);
          console.log(`Converted fighter1 odds from string "${importantStats[oddsStatIndex].fighter1Value}" to American ${oddsStat.fighter1Value}`);
        } else {
          // Not in American format and not a valid decimal odds - mark as N/A
          console.warn(`Invalid fighter1 odds format: ${oddsStat.fighter1Value}`);
          oddsStat.fighter1Value = "N/A";
        }
      }
    }
    
    // Make sure fighter2 odds are properly converted
    if (typeof oddsStat.fighter2Value === 'number') {
      // Direct conversion from decimal odds
      oddsStat.fighter2Value = convertToAmericanOdds(oddsStat.fighter2Value);
      console.log(`Converted fighter2 odds from decimal ${importantStats[oddsStatIndex].fighter2Value} to American ${oddsStat.fighter2Value}`);
    } else if (typeof oddsStat.fighter2Value === 'string') {
      // Check if already in American format (starts with + or -)
      if (oddsStat.fighter2Value.startsWith('+') || oddsStat.fighter2Value.startsWith('-')) {
        // Already in American format, don't convert
        console.log(`Fighter2 odds already in American format: ${oddsStat.fighter2Value}`);
      } else {
        // Try to parse as decimal number then convert
        const num = parseFloat(oddsStat.fighter2Value);
        if (!isNaN(num) && num > 1) {
          oddsStat.fighter2Value = convertToAmericanOdds(num);
          console.log(`Converted fighter2 odds from string "${importantStats[oddsStatIndex].fighter2Value}" to American ${oddsStat.fighter2Value}`);
        } else {
          // Not in American format and not a valid decimal odds - mark as N/A
          console.warn(`Invalid fighter2 odds format: ${oddsStat.fighter2Value}`);
          oddsStat.fighter2Value = "N/A";
        }
      }
    }
    
    // Add detailed logging for debugging
    console.log("Converting odds to American format:", {
      rawOdds: importantStats[oddsStatIndex],
      rawType1: typeof importantStats[oddsStatIndex].fighter1Value,
      rawType2: typeof importantStats[oddsStatIndex].fighter2Value,
      formattedOdds: oddsStat,
      formattedType1: typeof oddsStat.fighter1Value,
      formattedType2: typeof oddsStat.fighter2Value
    });
    
    // Only add the odds stat if at least one fighter has valid odds
    if (oddsStat.fighter1Value !== "N/A" || oddsStat.fighter2Value !== "N/A") {
      orderedStats.push(oddsStat);
    }
  }
  
  // Add the other stats in order if they exist
  const addStatByIndex = (index: number, newName: string) => {
    if (index !== -1) {
      // Clone the stat and update the name
      const renamedStat = { ...importantStats[index] };
      renamedStat.name = newName;
      orderedStats.push(renamedStat);
    } else {
      // Add placeholder if stat not found
      orderedStats.push({
        name: newName,
        fighter1Value: "N/A",
        fighter2Value: "N/A",
        unit: ""
      });
    }
  };
  
  // Add the remaining stats in our specified order
  addStatByIndex(strikesLandedIndex, "Strikes Landed");
  addStatByIndex(strikesAbsorbedIndex, "Strikes Absorbed");
  addStatByIndex(takedownAccuracyIndex, "TD Accuracy");
  addStatByIndex(strikingAccuracyIndex, "Striking Accuracy");
  
  // Use the ordered stats
  const topStats = orderedStats;
  
  // Determine predicted winner based on win probability
  const isFighter1PredictedWinner = fighter1.winProbability > fighter2.winProbability;
  const isFighter2PredictedWinner = fighter2.winProbability > fighter1.winProbability;
  
  // Icons for each stat type
  const getIconForStat = (statName: string, index: number) => {
    const iconSize = 24;
    const iconWeight = "regular";
    
    if (statName.toLowerCase().includes('odds')) 
      return <DiceThree size={iconSize} weight={iconWeight} />;
    if (statName.toLowerCase().includes('strikes landed')) 
      return <HandFist size={iconSize} weight={iconWeight} />;
    if (statName.toLowerCase().includes('absorbed')) 
      return <SmileyXEyes size={iconSize} weight={iconWeight} />;
    if (statName.toLowerCase().includes('takedown')) 
      return <Strategy size={iconSize} weight={iconWeight} />;
    if (statName.toLowerCase().includes('striking accuracy')) 
      return <Target size={iconSize} weight={iconWeight} />;
    if (statName.toLowerCase().includes('defense') || statName.toLowerCase().includes('accuracy')) 
      return <ShieldStar size={iconSize} weight={iconWeight} />;
    
    // Fallback to default icons based on position
    switch (index) {
      case 0: return <DiceThree size={iconSize} weight={iconWeight} />;
      case 1: return <HandFist size={iconSize} weight={iconWeight} />;
      case 2: return <SmileyXEyes size={iconSize} weight={iconWeight} />;  
      case 3: return <Strategy size={iconSize} weight={iconWeight} />;
      default: return <ShieldStar size={iconSize} weight={iconWeight} />;
    }
  };
  
  // Add interface for statistical insights
  type StatisticalInsights = string[] | StatisticalInsightsObject;

  // Existing code for the Statistical Insights section
  const renderStatisticalInsights = () => {
    if (selectedFight?.statisticalInsights) {
      // Handle both formats for backward compatibility 
      const insightsData = selectedFight.statisticalInsights as StatisticalInsights;
      
      const insights = Array.isArray(insightsData) 
        ? insightsData 
        : insightsData.insights || [];
        
      const title = Array.isArray(insightsData)
        ? "Statistical Insights"
        : insightsData.title || "Statistical Insights";
      
      if (insights && insights.length > 0) {
        return (
          <div className="shadcn-card p-6 rounded-3xl">
            <h3 className="text-sm font-medium text-text-tertiary dark:text-dark-text-tertiary mb-2">
              {title}
            </h3>
            <ul className="list-disc pl-5 text-text-primary dark:text-dark-text-primary">
              {insights.map((insight: string, index: number) => (
                <li key={`insight-${index}`} className="mb-2">
                  {insight}
                </li>
              ))}
            </ul>
          </div>
        );
      }
    }
    return null;
  };
  
  return (
    <div className="flex flex-col h-full overflow-visible">
      <div className={`flex-1 overflow-y-auto overflow-x-visible p-4`}>
        <div className="md:m-4 space-y-4 md:space-y-6 mb-12 md:mb-12 md:mt-3 overflow-visible">
          {/* Fighter comparison with radial charts */}
          <div className="flex justify-between items-start rounded-lg p-4 md:p-6 pt-2 md:pt-8 overflow-visible">
            <div className="text-center flex flex-col items-center w-[42%] md:w-72 overflow-visible">
              <div className="flex justify-center mb-2 h-32 md:h-44 overflow-visible">
                <FighterRadialChart
                  key={`fighter1-chart-${selectedFightIndex}-${fighter1.winProbability}`}
                  percentage={fighter1.winProbability}
                  color={fighter1WinnerColor}
                  position="left"
                  size={window.innerWidth < 768 ? 112 : 140}
                  strokeWidth={window.innerWidth < 768 ? 9 : 12}
                />
              </div>
              <h2 className="text-xl md:text-3xl font-semibold text-text-primary dark:text-dark-text-primary mb-1 md:mb-2 truncate w-full">
                {fighter1.name}
              </h2>
              <p className="text-xs md:text-sm text-text-tertiary dark:text-dark-text-tertiary">
                {fighter1.record && fighter1.record !== "TBD" ? fighter1.record.replace(/-/g, ' - ') : "TBD"}
              </p>
            </div>
            
            <div className="text-center text-text-tertiary dark:text-dark-text-tertiary self-center flex flex-col items-center justify-center mx-2 md:mx-4">
              <div className="border-l border-border dark:border-dark-border h-8 md:h-10 mb-2 md:mb-3" style={{ borderLeftWidth: '1px' }}></div>
              <span className="text-sm md:text-base my-1 md:my-2">vs</span>
              <div className="border-l border-border dark:border-dark-border h-8 md:h-10 mt-2 md:mt-4" style={{ borderLeftWidth: '1px' }}></div>
            </div>
            
            <div className="text-center flex flex-col items-center w-[42%] md:w-72 overflow-visible">
              <div className="flex justify-center mb-2 h-32 md:h-44 overflow-visible">
                <FighterRadialChart
                  key={`fighter2-chart-${selectedFightIndex}-${fighter2.winProbability}`}
                  percentage={fighter2.winProbability}
                  color={fighter2WinnerColor}
                  position="right"
                  size={window.innerWidth < 768 ? 112 : 140}
                  strokeWidth={window.innerWidth < 768 ? 9 : 12}
                />
              </div>
              <h2 className="text-xl md:text-3xl font-semibold text-text-primary dark:text-dark-text-primary mb-1 md:mb-2 truncate w-full">
                {fighter2.name}
              </h2>
              <p className="text-xs md:text-sm text-text-tertiary dark:text-dark-text-tertiary">
                {fighter2.record && fighter2.record !== "TBD" ? fighter2.record.replace(/-/g, ' - ') : "TBD"}
              </p>
            </div>
          </div>
          
          {/* Explanation card */}
          <div className="shadcn-card p-4 md:p-6 rounded-3xl">
            <p className="text-text-primary dark:text-dark-text-primary text-sm md:text-base">
              {narrative && narrative.length > 50 ? narrative : explanation}
            </p>
          </div>
          
          {/* Stats grid with individual cards */}
          <div className="grid grid-cols-2 gap-2 md:gap-6 gap-x-4 mt-8">
            {/* Left column - Fighter 1 stats */}
            <div className="space-y-2">
              {/* Fighter 1 name card at the top of the column */}
              <div className="shadcn-card p-4 md:px-6 rounded-t-3xl rounded-b-md flex items-center">
                {isFighter1PredictedWinner && (
                  <div className="w-2 h-2 mr-1.5 rounded-full bg-fighter-win flex-shrink-0"></div>
                )}
                <div className="text-sm font-medium text-text-tertiary dark:text-dark-text-tertiary">
                  {fighter1.name}
                </div>
              </div>
              
              {topStats.map((stat, index) => {
                // Determine if fighter1 is winning for this specific stat (for bar visualization only)
                const valueNum = typeof stat.fighter1Value === 'number' 
                  ? stat.fighter1Value 
                  : parseFloat(stat.fighter1Value as string) || 0;
                
                const otherValueNum = typeof stat.fighter2Value === 'number' 
                  ? stat.fighter2Value 
                  : parseFloat(stat.fighter2Value as string) || 0;
                
                const isOddsMetric = stat.name.toLowerCase().includes('odds');
                
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const hasStatAdvantage = isOddsMetric 
                  ? valueNum < otherValueNum 
                  : valueNum > otherValueNum;
                
                // Determine card position - now all are middle except the last one
                let position: 'first' | 'middle' | 'last' | 'single';
                if (topStats.length === 1) {
                  position = 'single';
                } else if (index === topStats.length - 1) {
                  position = 'last';
                } else {
                  position = 'middle';
                }
                
                return (
                  <StatCard
                    key={`fighter1-stat-${index}-${selectedFightIndex}`}
                    statName={stat.name}
                    value={stat.fighter1Value}
                    otherValue={stat.fighter2Value}
                    unit={stat.unit}
                    icon={getIconForStat(stat.name, index)}
                    color={grayColor}
                    isWinning={isFighter1PredictedWinner}
                    position={position}
                  />
                );
              })}
            </div>
            
            {/* Right column - Fighter 2 stats */}
            <div className="space-y-2">
              {/* Fighter 2 name card at the top of the column */}
              <div className="shadcn-card p-4 md:px-6 rounded-t-3xl rounded-b-md flex items-center">
                {isFighter2PredictedWinner && (
                  <div className="w-2 h-2 mr-1.5 rounded-full bg-fighter-win flex-shrink-0"></div>
                )}
                <div className="text-sm font-medium text-text-tertiary dark:text-dark-text-tertiary">
                  {fighter2.name}
                </div>
              </div>
              
              {topStats.map((stat, index) => {
                // Determine if fighter2 is winning for this specific stat (for bar visualization only)
                const valueNum = typeof stat.fighter2Value === 'number' 
                  ? stat.fighter2Value 
                  : parseFloat(stat.fighter2Value as string) || 0;
                
                const otherValueNum = typeof stat.fighter1Value === 'number' 
                  ? stat.fighter1Value 
                  : parseFloat(stat.fighter1Value as string) || 0;
                
                const isOddsMetric = stat.name.toLowerCase().includes('odds');
                
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const hasStatAdvantage = isOddsMetric 
                  ? valueNum < otherValueNum 
                  : valueNum > otherValueNum;
                
                // Determine card position - now all are middle except the last one
                let position: 'first' | 'middle' | 'last' | 'single';
                if (topStats.length === 1) {
                  position = 'single';
                } else if (index === topStats.length - 1) {
                  position = 'last';
                } else {
                  position = 'middle';
                }
                
                return (
                  <StatCard
                    key={`fighter2-stat-${index}-${selectedFightIndex}`}
                    statName={stat.name}
                    value={stat.fighter2Value}
                    otherValue={stat.fighter1Value}
                    unit={stat.unit}
                    icon={getIconForStat(stat.name, index)}
                    color={grayColor}
                    isWinning={isFighter2PredictedWinner}
                    position={position}
                  />
                );
              })}
            </div>
          </div>
          
          {/* Statistical Insights card - only shown if insights are available */}
          {renderStatisticalInsights()}
          
          {/* Spacer div - hidden on mobile */}
          <div className="hidden md:block h-2"></div>
          
          {/* Navigation and Disclaimer */}
          <div className="flex items-center justify-between px-4 pb-16 md:pb-0">
            <button 
              onClick={navigateToPrevFighter}
              disabled={selectedFightIndex === 0}
              className={`p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-primary dark:text-dark-text-primary active:scale-95 hidden md:block ${
                selectedFightIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              aria-label="Previous fighter"
            >
              <CaretUp className="h-6 w-6" weight="regular" />
            </button>
            
            <div className="flex flex-col items-center justify-center max-w-[320px] w-full mx-auto md:mx-20">
              <p className="text-xs text-text-tertiary dark:text-dark-text-tertiary text-center w-full">
                Use at your own discretion. Correct predictions are not guaranteed.{' '}
                <Link 
                  to="/methodology"
                  className="border-b border-zinc-300 dark:border-zinc-700 hover:border-zinc-500 dark:hover:border-zinc-500 transition-colors"
                >
                  Learn how the model works
                </Link>
                .
              </p>
            </div>
            
            <button 
              onClick={navigateToNextFighter}
              disabled={selectedFightIndex === fightPredictions.length - 1}
              className={`p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-primary dark:text-dark-text-primary active:scale-95 hidden md:block ${
                selectedFightIndex === fightPredictions.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              aria-label="Next fighter"
            >
              <CaretDown className="h-6 w-6" weight="regular" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FighterComparison;